/**
 * Frontend Type Definitions
 * Shared types for the FinanceFlow frontend application.
 */

export type TransactionType = 'INCOME' | 'EXPENSE';

// Currency Types
export type SupportedCurrency = 'BRL' | 'USD' | 'EUR' | 'GBP';

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['BRL', 'USD', 'EUR', 'GBP'];

export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  BRL: 'R$',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export const CURRENCY_NAMES: Record<SupportedCurrency, string> = {
  BRL: 'Real Brasileiro',
  USD: 'Dólar Americano',
  EUR: 'Euro',
  GBP: 'Libra Esterlina',
};



// Default exchange rates (fallback when database rates unavailable)
// Rates represent: 1 BRL = X foreign currency
export const DEFAULT_EXCHANGE_RATES: Record<SupportedCurrency, number> = {
  BRL: 1,
  USD: 0.20,
  EUR: 0.16,
  GBP: 0.15,
};

/**
 * Currency snapshot for storing exchange rates at transaction time.
 * Format: { BRL: 1, USD: 0.17, EUR: 0.16, GBP: 0.14 }
 * These rates represent: 1 BRL = X currency
 */
export type CurrencySnapshot = Record<string, number>;

/**
 * Entry with currency and snapshot data
 * Amount is always stored in BRL, with snapshot of exchange rates at entry time
 */
export interface CurrencyEntry {
  amount: number; // Amount in BRL
  currency: string; // Original input currency
  currencySnapshots: CurrencySnapshot; // Exchange rates at entry time
}

/**
 * Convert an entry's amount to the target currency using the stored currency snapshots.
 *
 * The amount is ALWAYS stored in BRL.
 * The currencySnapshots contains rates at the time of entry: { BRL: 1, USD: 0.17, EUR: 0.16, GBP: 0.14 }
 * These rates represent: 1 BRL = X currency
 *
 * Formula: DisplayValue = amountBRL * currencySnapshots[targetCurrency]
 *
 * @param entry Entry with amount (in BRL) and currencySnapshots
 * @param targetCurrency Target currency to convert to
 * @returns Amount in target currency
 */
export function convertEntryToTargetCurrency(
  entry: CurrencyEntry,
  targetCurrency: SupportedCurrency
): number {
  const { amount, currencySnapshots } = entry;

  // Get the target currency rate from the snapshot
  const rate = currencySnapshots?.[targetCurrency] ?? DEFAULT_EXCHANGE_RATES[targetCurrency];

  // Amount is in BRL, multiply by rate to get target currency
  // DisplayValue = amountBRL * rate
  return amount * rate;
}

/**
 * Calculate total of entries in the target currency using stored currency snapshots.
 *
 * @param entries Array of entries with amount (in BRL) and currencySnapshots
 * @param targetCurrency Target currency for the total
 * @returns Total amount in target currency
 */
export function calculateTotalInCurrency(
  entries: CurrencyEntry[],
  targetCurrency: SupportedCurrency
): number {
  return entries.reduce((sum, entry) => {
    return sum + convertEntryToTargetCurrency(entry, targetCurrency);
  }, 0);
}

/**
 * Convert a BRL amount to the target currency using provided or default rates.
 * Prefer using rates fetched from the database via useExchangeRates hook.
 *
 * @param amountBRL Amount in BRL to convert
 * @param targetCurrency Target currency
 * @param rates Optional exchange rates from database. Falls back to DEFAULT_EXCHANGE_RATES.
 * @returns Amount in target currency
 */
export function convertFromBRL(
  amountBRL: number,
  targetCurrency: SupportedCurrency,
  rates?: Record<SupportedCurrency, number> | null
): number {
  const rate = rates?.[targetCurrency] ?? DEFAULT_EXCHANGE_RATES[targetCurrency];
  return amountBRL * rate;
}

/**
 * Format a currency amount with the appropriate symbol
 * @param amount Amount to format
 * @param currency Currency code
 * @returns Formatted string like "$ 100.00" or "R$ 500,00"
 */
export function formatCurrencyWithSymbol(amount: number, currency: SupportedCurrency): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  const formatted = amount.toLocaleString(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formatted}`;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

export interface Category {
  name: string;
  nameKey: string;
  color: string;
}

export interface Transaction {
  id: string;
  amount: number; // Amount in BRL
  description: string;
  type: TransactionType;
  date: string;
  currency: SupportedCurrency; // Original input currency
  currencySnapshots: CurrencySnapshot; // Exchange rates at transaction time
  categoryId: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionWithCategory extends Transaction {
  category: Category;
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  success: true;
  data: T[];
  pagination: Pagination;
}

export interface ExpenseByCategory {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  totalAmount: number;
  transactionCount: number;
  percentage: number;
}

export interface TransactionSummary {
  totalIncome: number;
  totalExpenses: number;
  netBalance: number;
  transactionCount: number;
}

export interface ExpensesByCategoryResponse {
  success: true;
  data: {
    expenses: ExpenseByCategory[];
    summary: TransactionSummary;
    period: {
      startDate: string;
      endDate: string;
    };
  };
}

export interface RecentTransactionsResponse {
  success: true;
  data: TransactionWithCategory[];
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

export interface AuthResponse {
  success: true;
  data: {
    user: User;
    accessToken: string;
    expiresIn: number;
  };
}

export interface CreateTransactionInput {
  amount: number;
  description: string;
  type: TransactionType;
  date?: string;
  currency?: SupportedCurrency;
  categoryId: string;
}

export interface TransactionFilters {
  type?: TransactionType;
  categoryId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

// User Preferences Types
export type Theme = 'LIGHT' | 'DARK';
export type Language = 'EN' | 'PT_BR';

export interface BudgetCategoryConfig {
  id: string;
  name: string;
  nameKey: string;
  color: string;
  percentage: number;
}

export interface UserPreferences {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  theme: Theme;
  language: Language;
  defaultCurrency: SupportedCurrency;
  budgetConfig: {
    totalBudget: number;
    categories: BudgetCategoryConfig[];
  };
}

export interface CategoryPercentageUpdate {
  id?: string;
  nameKey: string;
  percentage: number;
}

export interface UpdatePreferencesInput {
  theme?: Theme;
  language?: Language;
  defaultCurrency?: SupportedCurrency;
  budgetConfig?: {
    totalBudget?: number;
    categories?: CategoryPercentageUpdate[];
  };
}

// Report Types
export interface MonthlyReportCategory {
  categoryId: string;
  categoryName: string;
  categoryNameKey: string;
  categoryColor: string;
  total: number;
}

export interface MonthlyReportData {
  month: number;
  monthName: string;
  categories: MonthlyReportCategory[];
  totalExpenses: number;
  totalIncome: number;
}

export interface YearlyReport {
  year: number;
  months: MonthlyReportData[];
  yearlyTotals: {
    totalExpenses: number;
    totalIncome: number;
    netBalance: number;
  };
}

export interface YearlyReportResponse {
  success: true;
  data: YearlyReport;
}

export interface UserPreferencesResponse {
  success: true;
  data: UserPreferences;
}
